import PublicLayout from '@layouts/PublicLayout';
import { auth } from '@services/firebase/config';
import { applyActionCode, checkActionCode } from 'firebase/auth';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [email, setEmail] = useState('');

  const oobCode = searchParams.get('oobCode');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!oobCode) {
        setError('No se proporcionó un código de verificación');
        setIsLoading(false);
        return;
      }

      try {
        // Primero verificamos que el codigo sea valido
        const info = await checkActionCode(auth, oobCode);
        setEmail(info.data.email || '');

        // Si el codigo es valido, aplicamos la verificacion
        await applyActionCode(auth, oobCode);
        setSuccess('Tu correo electrónico ha sido verificado correctamente');
      } catch (error) {
        console.error('Error al verificar el email:', error);
        setError(
          'El código de verificación no es válido o ha expirado. Por favor, solicita un nuevo enlace de verificación.'
        );
      } finally {
        setIsLoading(false);
      }
    };

    verifyEmail();
  }, [oobCode]);

  return (
    <PublicLayout title="Verificar correo electrónico">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-card border border-border shadow rounded-xl"
        >
          <div className="px-4 py-5 sm:p-6">
            <div className="text-center">
              {isLoading ? (
                <>
                  <h2 className="text-lg font-medium text-foreground mb-4">
                    Verificando tu correo electrónico
                  </h2>
                  <svg
                    className="animate-spin h-8 w-8 text-primary mx-auto"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </>
              ) : error ? (
                <>
                  <h2 className="text-lg font-medium text-destructive mb-4">
                    Error de verificación
                  </h2>
                  <p className="text-sm text-muted-foreground mb-4">{error}</p>
                  <button
                    onClick={() => navigate('/login')}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Volver al inicio de sesión
                  </button>
                </>
              ) : (
                <>
                  <svg
                    className="h-12 w-12 text-green-500 mx-auto mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h2 className="text-lg font-medium text-foreground mb-2">
                    ¡Verificación exitosa!
                  </h2>
                  <p className="text-sm text-muted-foreground mb-4">
                    {success}
                    {email && (
                      <>
                        <br />
                        Correo verificado: <strong>{email}</strong>
                      </>
                    )}
                  </p>
                  <button
                    onClick={() => navigate('/login')}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Volver al inicio de sesión
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </PublicLayout>
  );
}
